import DimensionDO = App.Data.DimensionDO;

export type RailItem = {
	dimension: DimensionDO;
	title: string;
	isPinned: boolean;
};

export type RailGroup = {
	key: string;
	label: string;
	items: RailItem[];

	isPinned: boolean;
};

export type Translator = (key: string) => string;

const ucfirst = (value: string): string =>
	value.length === 0 ? value : value[0].toUpperCase() + value.slice(1);

export const dimensionTitle = (t: Translator, dimension: DimensionDO): string =>
	ucfirst(t(`properties.${dimension.name_clean}.title`));

/** Segment dimensions sit in the `customer` family, which would bury them. */
const groupKeyOf = (dimension: DimensionDO): string =>
	dimension.type === "segment"
		? "segment"
		: dimension.family_name_clean ?? "";

export const familyLabel = (t: Translator, dimension: DimensionDO): string =>
	ucfirst(t(`property_families.${groupKeyOf(dimension)}.title`));

const matches = (
	t: Translator,
	dimension: DimensionDO,
	query: string
): boolean => {
	if (query === "") {
		return true;
	}

	return (
		dimensionTitle(t, dimension).toLowerCase().includes(query) ||
		familyLabel(t, dimension).toLowerCase().includes(query)
	);
};

export const buildRailGroups = (
	dimensions: DimensionDO[],
	{
		pins = [],
		query = "",
		t,
	}: { pins?: string[]; query?: string; t: Translator }
): RailGroup[] => {
	const needle = query.trim().toLowerCase();
	const pinned = new Set(pins);

	const items = dimensions
		.filter((dimension) => matches(t, dimension, needle))
		.map((dimension) => ({
			dimension,
			title: dimensionTitle(t, dimension),
			isPinned: pinned.has(dimension.name_clean),
		}));

	const families = new Map<string, RailGroup>();

	items.forEach((item) => {
		const key = groupKeyOf(item.dimension);

		if (!families.has(key)) {
			families.set(key, {
				key,
				label: familyLabel(t, item.dimension),
				items: [],
				isPinned: false,
			});
		}

		families.get(key)!.items.push(item);
	});

	// Deliberately unsorted: the API orders by family then `sort_order` already.
	const groups = [...families.values()];

	const pinnedItems = items.filter((item) => item.isPinned);

	if (pinnedItems.length === 0 || needle !== "") {
		return groups;
	}

	return [
		{ key: "__pinned", label: "", items: pinnedItems, isPinned: true },
		...groups,
	];
};

export const groupsToExpand = (
	groups: RailGroup[],
	{
		query = "",
		selectedNameClean = null,
	}: { query?: string; selectedNameClean?: string | null }
): string[] => {
	if (query.trim() !== "") {
		return groups.map((group) => group.key);
	}

	if (!selectedNameClean) {
		return [];
	}

	return groups
		.filter(
			(group) =>
				group.items.some(
					(item) => item.dimension.name_clean === selectedNameClean
				) && group.key !== "__pinned"
		)
		.map((group) => group.key);
};
